import { useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  computeCalculator,
  findRyczaltOptionByPercent,
  formatPLN,
  formatPercent,
  parsePLNumber,
  RYCZALT_OPTIONS,
} from "@/lib/taxCalculator";

const INDUSTRY_CHOICES = [
  { id: "tfa_97", label: "Architektura i inżynieria, projektowanie", percent: 14 },
  { id: "tfa_61", label: "Budownictwo", percent: 5.5 },
  { id: "tfa_65", label: "Doradztwo", percent: 15 },
  { id: "tfa_63", label: "Gastronomia", percent: 0, conditional: "gastronomia" },
  { id: "tfa_66", label: "HR, Turystyka, Sport, Kultura i Rozrywka", percent: 15 },
  { id: "tfa_60", label: "Informatyka", percent: 0, conditional: "informatyka" },
  { id: "tfa_98", label: "Kupno i sprzedaż nieruchomości na własny rachunek", percent: 10 },
  { id: "tfa_96", label: "Opieka zdrowotna (PKD 86)", percent: 14 },
  { id: "tfa_56", label: "Pośrednictwo handlowe", percent: 15 },
  { id: "tfa_57", label: "Reklama, Fotografia, Obsługa nieruchomości na zlecenie", percent: 15 },
  { id: "tfa_64", label: "Sprzątanie", percent: 8.5 },
  { id: "tfa_62", label: "Transport", percent: 8.5 },
  { id: "tfa_55", label: "Wolny zawód", percent: 17, conditional: "wolnyzawod" },
  { id: "tfa_105", label: "Wynajem, Zakwaterowanie", percent: 0, conditional: "wynajem" },
  { id: "tfa_108", label: "Księgowość, prawo (inne niż w ramach wolnych zawodów)", percent: 15 },
  { id: "tfa_67", label: "Inna", percent: 0, conditional: "inna" },
  { id: "tfa_115", label: "Handel", percent: 0, conditional: "handel" },
];

const SELECTS = {
  informatyka: {
    label: "Informatyka",
    placeholder: "Proszę wybrać...",
    options: [
      { id: "tfa_73", label: "Związana z programowaniem", percent: 12, hideRyczaltChoice: false },
      {
        id: "tfa_74",
        label: "Związana z doradztwem w zakresie sprzętu, zarządzaniem siecią i systemami",
        percent: 8.5,
        hideRyczaltChoice: true,
      },
    ],
  },
  wynajem: {
    label: "Wynajem, zakwaterowanie",
    placeholder: "Proszę wybrać...",
    options: [
      { id: "tfa_103", label: "do 100 tyś rocznie", percent: 8.5 },
      { id: "tfa_104", label: "Powyżej 100 rocznie", percent: 12.5 },
    ],
  },
  gastronomia: {
    label: "Gastronomia",
    placeholder: "Proszę wybrać...",
    options: [
      {
        id: "tfa_113",
        label: "w zakresie sprzedaży napojów o zawartości alkoholu powyżej 1,5%",
        percent: 8.5,
      },
      {
        id: "tfa_114",
        label: "z wyjątkiem przychodów ze sprzedaży napojów o zawartości alkoholu powyżej 1,5%",
        percent: 3,
      },
    ],
  },
  handel: {
    label: "Handel",
    placeholder: "Proszę wybrać...",
    options: [
      { id: "tfa_120", label: "pośrednictwo w sprzedaży hurtowej", percent: 15 },
      { id: "tfa_121", label: "handel własnym (zakupywanym towarem)", percent: 3 },
    ],
  },
};

function Card({ title, description, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="rounded-2xl border bg-card text-card-foreground shadow-sm"
    >
      <div className="border-b px-6 py-4">
        <h2 className="text-base font-semibold leading-6" data-testid={`${testId}-title`}>
          {title}
        </h2>
        {description ? (
          <p className="mt-1 text-sm text-muted-foreground" data-testid={`${testId}-description`}>
            {description}
          </p>
        ) : null}
      </div>
      <div className="px-6 py-5">{children}</div>
    </section>
  );
}

function FieldLabel({ htmlFor, children, testId }) {
  return (
    <label
      htmlFor={htmlFor}
      className="mb-1 block text-sm font-medium"
      data-testid={testId}
    >
      {children}
    </label>
  );
}

function helperPercentFromSelect(selectKey, optionId) {
  if (!selectKey || !optionId) return 0;
  const opt = SELECTS[selectKey]?.options?.find((o) => o.id === optionId);
  return opt?.percent ?? 0;
}

export default function CalculatorPage() {
  const [step, setStep] = useState(1);

  // Step 1 (Branża)
  const [industryId, setIndustryId] = useState("");
  const [informatykaOptionId, setInformatykaOptionId] = useState("");
  const [wynajemOptionId, setWynajemOptionId] = useState("");
  const [gastronomiaOptionId, setGastronomiaOptionId] = useState("");
  const [handelOptionId, setHandelOptionId] = useState("");
  const [innaText, setInnaText] = useState("");

  // Step 2 (Kalkulator)
  const [ryczaltRate, setRyczaltRate] = useState(null);
  const [przychodMonthlyStr, setPrzychodMonthlyStr] = useState("");
  const [kosztyMonthlyStr, setKosztyMonthlyStr] = useState("");

  const [zusOptionId, setZusOptionId] = useState("");
  const [zusMalyStr, setZusMalyStr] = useState("433");
  const [zusDuzyStr, setZusDuzyStr] = useState("1694");


  const exportRef = useRef(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const [feedback, setFeedback] = useState("");
  const [submitError, setSubmitError] = useState("");

  const selectedIndustry = useMemo(
    () => INDUSTRY_CHOICES.find((c) => c.id === industryId) ?? null,
    [industryId],
  );

  const procent = selectedIndustry?.percent ?? 0;
  const informatyka = helperPercentFromSelect("informatyka", informatykaOptionId);
  const wynajem = helperPercentFromSelect("wynajem", wynajemOptionId);
  const gastronomia = helperPercentFromSelect("gastronomia", gastronomiaOptionId);
  const handel = helperPercentFromSelect("handel", handelOptionId);

  const minimalRatePercent = useMemo(() => {
    return (procent || 0) + (informatyka || 0) + (wynajem || 0) + (gastronomia || 0) + (handel || 0);
  }, [procent, informatyka, wynajem, gastronomia, handel]);

  const informatykaSelectedOpt = useMemo(() => {
    return SELECTS.informatyka.options.find((o) => o.id === informatykaOptionId) ?? null;
  }, [informatykaOptionId]);

  const hideRyczaltChoice = Boolean(informatykaSelectedOpt?.hideRyczaltChoice);

  const suggestedRyczaltRate = useMemo(() => {
    if (minimalRatePercent > 0) return findRyczaltOptionByPercent(minimalRatePercent);
    return 0;
  }, [minimalRatePercent]);

  const effectiveRyczaltRate = useMemo(() => {
    if (hideRyczaltChoice) return 0.085;
    return ryczaltRate ?? suggestedRyczaltRate;
  }, [hideRyczaltChoice, ryczaltRate, suggestedRyczaltRate]);

  const przychodMonthly = useMemo(() => parsePLNumber(przychodMonthlyStr), [przychodMonthlyStr]);
  const kosztyMonthly = useMemo(() => parsePLNumber(kosztyMonthlyStr), [kosztyMonthlyStr]);
  const rocznyPrzychod = useMemo(() => przychodMonthly * 12, [przychodMonthly]);
  const roczneKoszty = useMemo(() => kosztyMonthly * 12, [kosztyMonthly]);

  const zusmalyMonthly = useMemo(() => {
    if (zusOptionId === "tfa_19") return parsePLNumber(zusMalyStr);
    return 0;
  }, [zusOptionId, zusMalyStr]);

  const zusduzyMonthly = useMemo(() => {
    if (zusOptionId === "tfa_20") return parsePLNumber(zusDuzyStr);
    return 0;
  }, [zusOptionId, zusDuzyStr]);

  const calc = useMemo(() => {
    return computeCalculator({
      ryczaltRate: effectiveRyczaltRate,
      przychodMonthly,
      kosztyMonthly,
      rocznyPrzychod,
      zusmalyMonthly,
      zusduzyMonthly,
    });
  }, [effectiveRyczaltRate, przychodMonthly, kosztyMonthly, rocznyPrzychod, zusmalyMonthly, zusduzyMonthly]);

  const bestOption = useMemo(() => {
    const candidates = [
      { key: "ryczalt", label: "Ryczałt", value: calc.zostajeRyczalt },
      { key: "ogolne", label: "Zasady ogólne", value: calc.zostajeOgolne },
      { key: "liniowy", label: "Liniówka", value: calc.zostajeLiniowy },
    ];
    return candidates.reduce((acc, cur) => (cur.value > acc.value ? cur : acc), candidates[0]);
  }, [calc.zostajeRyczalt, calc.zostajeOgolne, calc.zostajeLiniowy]);

  const bestTakeHomeKey = bestOption.key;

  const canGoNextFromStep1 = useMemo(() => {
    if (!industryId) return false;

    if (selectedIndustry?.conditional === "informatyka") return Boolean(informatykaOptionId);
    if (selectedIndustry?.conditional === "wynajem") return Boolean(wynajemOptionId);
    if (selectedIndustry?.conditional === "gastronomia") return Boolean(gastronomiaOptionId);
    if (selectedIndustry?.conditional === "handel") return Boolean(handelOptionId);
    if (selectedIndustry?.conditional === "inna") return innaText.trim().length > 1;

    return true;
  }, [industryId, selectedIndustry, informatykaOptionId, wynajemOptionId, gastronomiaOptionId, handelOptionId, innaText]);

  const handleStartOver = () => {
    setExportError("");
    setIsExporting(false);
    setStep(1);
    setIndustryId("");
    setInformatykaOptionId("");
    setWynajemOptionId("");
    setGastronomiaOptionId("");
    setHandelOptionId("");
    setInnaText("");

    setRyczaltRate(null);
    setPrzychodMonthlyStr("");
    setKosztyMonthlyStr("");
    setZusOptionId("");
    setZusMalyStr("433");
    setZusDuzyStr("1694");

    setFeedback("");
    setSubmitError("");
  };

  const handleCopySummary = async () => {
    const lines = [
      `Minimalna stawka (wg branży): ${formatPercent(minimalRatePercent)}`,
      `Przychód miesięczny: ${formatPLN(przychodMonthly)} zł`,
      `Koszty miesięczne: ${formatPLN(kosztyMonthly)} zł`,
      `ZUS (mies.): ${formatPLN(zusmalyMonthly + zusduzyMonthly)} zł`,
      `Ryczałt (stawka): ${formatPercent(effectiveRyczaltRate * 100)}`,
      `Zostaje na rękę (ryczałt): ${formatPLN(calc.zostajeRyczalt)} zł`,
      `Zostaje na rękę (ogólne): ${formatPLN(calc.zostajeOgolne)} zł`,
      `Zostaje na rękę (liniówka): ${formatPLN(calc.zostajeLiniowy)} zł`,
      `Najlepsza opcja: ${bestOption.label}`,
    ];
    await navigator.clipboard.writeText(lines.join("\n"));
  };

  const handleExportPdf = async () => {
    setExportError("");

    const node = exportRef.current;
    if (!node) {
      setExportError("Nie udało się znaleźć sekcji do eksportu.");
      return;
    }

    try {
      setIsExporting(true);

      // Render do canvas
      const canvas = await html2canvas(node, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");

      // A4 portrait: 210 x 297mm
      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Przeliczenie proporcji obrazu na mm
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      // Pierwsza strona
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      // Dodatkowe strony, jeśli zawartość jest dłuższa
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `kalkulator-opodatkowania-2026-jdg-${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(fileName);
    } catch (e) {
      console.error(e);
      setExportError("Nie udało się wygenerować PDF. Spróbuj ponownie.");
    } finally {
      setIsExporting(false);
    }
  };


  const handleFinish = () => {
    setSubmitError("");
    if (!feedback) {
      setSubmitError("Zostaw feedback (1–5), a następnie kliknij Zakończ.");
      return;
    }
    setStep(3);
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <header className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="page-title">
                Kalkulator formy opodatkowania 2026 dla JDG
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground" data-testid="page-subtitle">
                2‑krokowy kalkulator (Branża → Kalkulacja). To narzędzie ma charakter orientacyjny — wyniki mogą się różnić
                w zależności od interpretacji i szczegółów działalności, dlatego warto je zweryfikować z księgowym lub w aktualnych przepisach.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleStartOver}
                className="rounded-lg border px-3 py-2 text-sm hover:bg-muted"
                data-testid="reset-button"
              >
                Reset
              </button>
            </div>
          </div>

          <div className="mt-5 flex items-center gap-2" data-testid="step-indicator">
            <span
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                step === 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}
              data-testid="step-indicator-1"
            >
              1. Branża
            </span>
            <span
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                step === 2 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}
              data-testid="step-indicator-2"
            >
              2. Kalkulacja
            </span>
            <span
              className={`rounded-full px-3 py-1 text-xs font-medium ${
                step === 3 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
              }`}
              data-testid="step-indicator-3"
            >
              3. Zakończone
            </span>
          </div>
        </header>

        {step === 1 ? (
          <div className="space-y-6" data-testid="step-1">
            <Card
              title="Określ branżę"
              description="Wybierz branżę oraz — jeśli dotyczy — doprecyzuj wariant (np. Informatyka / Handel)."
              testId="industry-card"
            >
              <div role="radiogroup" aria-label="Określ branżę" className="grid gap-3" data-testid="industry-radiogroup">
                {INDUSTRY_CHOICES.map((c) => (
                  <label
                    key={c.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition hover:bg-muted ${
                      industryId === c.id ? "border-primary" : "border-border"
                    }`}
                    data-testid={`industry-option-${c.id}`}
                  >
                    <input
                      type="radio"
                      name="industry"
                      value={c.id}
                      checked={industryId === c.id}
                      onChange={() => {
                        setIndustryId(c.id);
                        // reset pól warunkowych
                        setInformatykaOptionId("");
                        setWynajemOptionId("");
                        setGastronomiaOptionId("");
                        setHandelOptionId("");
                        setInnaText("");
                      }}
                      className="mt-1 h-4 w-4"
                      data-testid={`industry-option-${c.id}-radio`}
                    />
                    <span className="flex-1">
                      <span className="block text-sm font-medium" data-testid={`industry-option-${c.id}-label`}>
                        {c.label}
                      </span>
                      <span className="mt-1 block text-xs text-muted-foreground" data-testid={`industry-option-${c.id}-hint`}>
                        {c.percent ? `Stawka bazowa: ${formatPercent(c.percent)}` : "Wymaga doprecyzowania"}
                      </span>
                    </span>
                  </label>
                ))}
              </div>

              {selectedIndustry?.conditional === "wolnyzawod" ? (
                <div className="mt-5 rounded-xl border bg-muted/40 p-4 text-sm" data-testid="wolny-zawod-info">
                  <div className="font-medium" data-testid="wolny-zawod-info-title">Wolny zawód to:</div>
                  <div className="mt-2 text-muted-foreground" data-testid="wolny-zawod-info-text">
                    (tłumacz, adwokat, notariusz, radca prawny, biegły rewident, księgowy, agent ubezpieczeniowy,
                    broker reasekuracyjny, broker ubezpieczeniowy, doradca podatkowy, doradca restrukturyzacyjny,
                    makler papierów wartościowych, doradca inwestycyjny, agent firm inwestycyjnych oraz rzeczników patentowych)
                  </div>
                </div>
              ) : null}

              {selectedIndustry?.conditional === "informatyka" ? (
                <div className="mt-5" data-testid="informatyka-select-wrapper">
                  <FieldLabel htmlFor="informatyka" testId="informatyka-select-label">
                    Informatyka
                  </FieldLabel>
                  <select
                    id="informatyka"
                    className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                    value={informatykaOptionId}
                    onChange={(e) => setInformatykaOptionId(e.target.value)}
                    data-testid="informatyka-select"
                  >
                    <option value="">{SELECTS.informatyka.placeholder}</option>
                    {SELECTS.informatyka.options.map((o) => (
                      <option key={o.id} value={o.id} data-testid={`informatyka-select-option-${o.id}`}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {selectedIndustry?.conditional === "wynajem" ? (
                <div className="mt-5" data-testid="wynajem-select-wrapper">
                  <FieldLabel htmlFor="wynajem" testId="wynajem-select-label">
                    Wynajem, zakwaterowanie
                  </FieldLabel>
                  <select
                    id="wynajem"
                    className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                    value={wynajemOptionId}
                    onChange={(e) => setWynajemOptionId(e.target.value)}
                    data-testid="wynajem-select"
                  >
                    <option value="">{SELECTS.wynajem.placeholder}</option>
                    {SELECTS.wynajem.options.map((o) => (
                      <option key={o.id} value={o.id} data-testid={`wynajem-select-option-${o.id}`}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {selectedIndustry?.conditional === "gastronomia" ? (
                <div className="mt-5" data-testid="gastronomia-select-wrapper">
                  <FieldLabel htmlFor="gastronomia" testId="gastronomia-select-label">
                    Gastronomia
                  </FieldLabel>
                  <select
                    id="gastronomia"
                    className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                    value={gastronomiaOptionId}
                    onChange={(e) => setGastronomiaOptionId(e.target.value)}
                    data-testid="gastronomia-select"
                  >
                    <option value="">{SELECTS.gastronomia.placeholder}</option>
                    {SELECTS.gastronomia.options.map((o) => (
                      <option key={o.id} value={o.id} data-testid={`gastronomia-select-option-${o.id}`}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {selectedIndustry?.conditional === "handel" ? (
                <div className="mt-5" data-testid="handel-select-wrapper">
                  <FieldLabel htmlFor="handel" testId="handel-select-label">
                    Handel
                  </FieldLabel>
                  <select
                    id="handel"
                    className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                    value={handelOptionId}
                    onChange={(e) => setHandelOptionId(e.target.value)}
                    data-testid="handel-select"
                  >
                    <option value="">{SELECTS.handel.placeholder}</option>
                    {SELECTS.handel.options.map((o) => (
                      <option key={o.id} value={o.id} data-testid={`handel-select-option-${o.id}`}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {selectedIndustry?.conditional === "inna" ? (
                <div className="mt-5" data-testid="inna-text-wrapper">
                  <FieldLabel htmlFor="inna" testId="inna-text-label">
                    Napisz jaka:
                  </FieldLabel>
                  <input
                    id="inna"
                    className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                    value={innaText}
                    onChange={(e) => setInnaText(e.target.value)}
                    placeholder="Po zgłoszeniu dodamy Twoją branżę."
                    data-testid="inna-text-input"
                  />
                </div>
              ) : null}

              <div className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="minimal-rate-grid">
                <div className="rounded-xl border bg-muted/30 p-4" data-testid="minimal-rate-card">
                  <div className="text-xs font-medium text-muted-foreground" data-testid="minimal-rate-label">
                    Możliwa najniższa stawka podatku do zastosowania
                  </div>
                  <div className="mt-2 text-2xl font-semibold" data-testid="minimal-rate-value">
                    {minimalRatePercent ? formatPercent(minimalRatePercent) : "—"}
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground" data-testid="minimal-rate-hint">
                    Możesz przejść dalej do kalkulatora, bez konieczności wyboru stawki ryczałtu.
                  </div>
                </div>
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="step1-tip-card">
                  <div className="text-xs font-semibold text-amber-900" data-testid="step1-tip-title">
                    Uwaga
                  </div>
                  <div className="mt-2 text-sm text-amber-900/90" data-testid="step1-tip-text">
                    Pamiętaj, aby zweryfikować dokładnie możliwość zastosowania odpowiedniej formy opodatkowania.
                    <span className="block mt-2" data-testid="step1-tip-text-2025">
                      Od 2025 obowiązuje nowa klasyfikacja usług — warto zweryfikować właściwe PKWiU/PKD i stawkę ryczałtu.
                    </span>
                  </div>
                </div>
              </div>
            </Card>

            <div className="flex items-center justify-between" data-testid="step1-actions">
              <div className="text-sm text-muted-foreground" data-testid="step1-actions-note">
                Wymagane: branża + doprecyzowanie (jeśli dotyczy).
              </div>
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!canGoNextFromStep1}
                className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="step1-next-button"
              >
                Dalej
              </button>
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-6" data-testid="step-2">
            <Card
              title="Kalkulacja zysków"
              description="Wpisz przychody, koszty i ZUS. Wyniki będą aktualizować się automatycznie."
              testId="calc-card"
            >
              <div ref={exportRef} className="grid gap-6" data-testid="calc-form-grid">
                <div data-testid="ryczalt-section">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-sm font-medium" data-testid="ryczalt-title">Twoja stawka ryczałtu</div>
                      <div className="mt-1 text-xs text-muted-foreground" data-testid="ryczalt-subtitle">
                        {hideRyczaltChoice
                          ? "Dla wybranego wariantu Informatyki stawka jest ustawiona automatycznie (8,5%)."
                          : "Możesz zmienić stawkę, jeśli masz pewność co do PKD / stawki."}
                      </div>
                    </div>
                    <div className="rounded-xl border bg-muted/30 px-3 py-2" data-testid="ryczalt-suggested">
                      <div className="text-[11px] text-muted-foreground" data-testid="ryczalt-suggested-label">
                        Sugerowana (z kroku 1)
                      </div>
                      <div className="text-sm font-semibold" data-testid="ryczalt-suggested-value">
                        {minimalRatePercent ? formatPercent(minimalRatePercent) : "—"}
                      </div>
                      <div className="mt-1 text-[11px] text-muted-foreground" data-testid="ryczalt-effective-value">
                        Użyta w obliczeniach: <span className="font-medium">{formatPercent(effectiveRyczaltRate * 100)}</span>
                      </div>
                    </div>
                  </div>

                  {!hideRyczaltChoice ? (
                    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="ryczalt-options-grid">
                      {RYCZALT_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setRyczaltRate(opt.value)}
                          className={`rounded-lg border px-3 py-2 text-sm transition hover:bg-muted ${
                            ryczaltRate === opt.value ? "border-primary bg-muted" : "border-border"
                          }`}
                          data-testid={`ryczalt-option-${String(opt.value).replace(".", "_")}-button`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-3 rounded-xl border bg-muted/30 p-4" data-testid="ryczalt-auto-info">
                      <div className="text-sm">
                        Ustawiona stawka: <span className="font-semibold" data-testid="ryczalt-auto-value">8,5%</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid gap-4 sm:grid-cols-2" data-testid="income-costs-grid">
                  <div data-testid="income-input-wrapper">
                    <FieldLabel htmlFor="przychod" testId="income-input-label">
                      Wpisz Twój przychód miesięczny
                    </FieldLabel>
                    <input
                      id="przychod"
                      inputMode="decimal"
                      className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                      value={przychodMonthlyStr}
                      onChange={(e) => setPrzychodMonthlyStr(e.target.value)}
                      placeholder="np. 15000"
                      data-testid="income-input"
                    />
                  </div>
                  <div className="rounded-xl border bg-muted/30 p-4" data-testid="annual-income-card">
                    <div className="text-xs font-medium text-muted-foreground" data-testid="annual-income-label">
                      Przychód roczny
                    </div>
                    <div className="mt-2 text-xl font-semibold" data-testid="annual-income-value">
                      {formatPLN(rocznyPrzychod)} zł
                    </div>
                  </div>

                  <div data-testid="costs-input-wrapper">
                    <FieldLabel htmlFor="koszty" testId="costs-input-label">
                      Wpisz — Koszty miesięczne
                    </FieldLabel>
                    <input
                      id="koszty"
                      inputMode="numeric"
                      className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                      value={kosztyMonthlyStr}
                      onChange={(e) => setKosztyMonthlyStr(e.target.value)}
                      placeholder="np. 4000"
                      data-testid="costs-input"
                    />
                  </div>
                  <div className="rounded-xl border bg-muted/30 p-4" data-testid="annual-costs-card">
                    <div className="text-xs font-medium text-muted-foreground" data-testid="annual-costs-label">
                      Koszty roczne
                    </div>
                    <div className="mt-2 text-xl font-semibold" data-testid="annual-costs-value">
                      {formatPLN(roczneKoszty)} zł
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2" data-testid="zus-grid">
                  <div data-testid="zus-select-wrapper">
                    <FieldLabel htmlFor="zus" testId="zus-select-label">
                      ZUS
                    </FieldLabel>
                    <select
                      id="zus"
                      className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                      value={zusOptionId}
                      onChange={(e) => setZusOptionId(e.target.value)}
                      data-testid="zus-select"
                    >
                      <option value="">Proszę wybrać...</option>
                      <option value="tfa_18" data-testid="zus-option-tfa_18">
                        Pierwsze 6 miesięcy/lub — Tylko zdrowotne
                      </option>
                      <option value="tfa_19" data-testid="zus-option-tfa_19">
                        Mały zus preferencyjny
                      </option>
                      <option value="tfa_20" data-testid="zus-option-tfa_20">
                        ZUS w pełnej wysokości
                      </option>
                    </select>
                    <div className="mt-2 text-xs text-muted-foreground" data-testid="zus-hint">
                      Uwaga: w oryginalnym formularzu odliczenie w obliczeniach dotyczy pól „ZUS mały/duży” (społeczne).
                    </div>
                  </div>

                  <div className="grid gap-3" data-testid="zus-conditional-inputs">
                    {zusOptionId === "tfa_19" ? (
                      <div data-testid="zus-maly-wrapper">
                        <FieldLabel htmlFor="zusmaly" testId="zus-maly-label">
                          W koszty ZUS mały
                        </FieldLabel>
                        <input
                          id="zusmaly"
                          inputMode="decimal"
                          className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                          value={zusMalyStr}
                          onChange={(e) => setZusMalyStr(e.target.value)}
                          data-testid="zus-maly-input"
                        />
                      </div>
                    ) : null}

                    {zusOptionId === "tfa_20" ? (
                      <div data-testid="zus-duzy-wrapper">
                        <FieldLabel htmlFor="zusduzy" testId="zus-duzy-label">
                          W koszty ZUS duży
                        </FieldLabel>
                        <input
                          id="zusduzy"
                          inputMode="decimal"
                          className="w-full rounded-lg border bg-background px-3 py-2 text-sm"
                          value={zusDuzyStr}
                          onChange={(e) => setZusDuzyStr(e.target.value)}
                          data-testid="zus-duzy-input"
                        />
                      </div>
                    ) : null}

                    <div className="rounded-xl border bg-muted/30 p-4" data-testid="zus-total-card">
                      <div className="text-xs font-medium text-muted-foreground" data-testid="zus-total-label">
                        ZUS (uwzględniony w kosztach)
                      </div>
                      <div className="mt-2 text-xl font-semibold" data-testid="zus-total-value">
                        {formatPLN(zusmalyMonthly + zusduzyMonthly)} zł
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 rounded-2xl border bg-muted/20 p-5" data-testid="results-section">
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="export-actions">
                  <div className="text-xs text-muted-foreground" data-testid="export-hint">
                    Eksport obejmuje sekcję kalkulatora (wraz z wynikami).
                  </div>
                  <button
                    type="button"
                    onClick={handleExportPdf}
                    disabled={isExporting}
                    className="rounded-lg border px-4 py-2 text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="export-pdf-button"
                  >
                    {isExporting ? "Generowanie PDF..." : "Eksportuj PDF"}
                  </button>
                </div>

                {exportError ? (
                  <div
                    className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm"
                    data-testid="export-error"
                  >
                    {exportError}
                  </div>
                ) : null}
                <div className="flex items-start justify-between gap-4" data-testid="results-header">
                  <div>
                    <h3 className="text-sm font-semibold" data-testid="results-title">Nowy ŁAD 2.0</h3>
                    <p className="mt-1 text-xs text-muted-foreground" data-testid="results-subtitle">
                      Wszystkie wartości są liczone automatycznie (miesięcznie), zgodnie z formułami z formularza.
                    </p>
                  </div>
                  <div className="rounded-xl border bg-background px-3 py-2" data-testid="best-option-badge">
                    <div className="text-[11px] text-muted-foreground" data-testid="best-option-label">
                      Najlepsza opcja (zostaje najwięcej)
                    </div>
                    <div className="text-sm font-semibold" data-testid="best-option-value">
                      {bestOption.label}
                    </div>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 lg:grid-cols-3" data-testid="tax-grid">
                  <div className="rounded-xl border bg-background p-4" data-testid="tax-ryczalt-card">
                    <div className="text-xs font-medium text-muted-foreground" data-testid="tax-ryczalt-label">
                      Podatek do zapłaty (ryczałt)
                    </div>
                    <div className="mt-2 text-xl font-semibold" data-testid="tax-ryczalt-value">
                      {formatPLN(calc.nowepodatek)} zł
                    </div>
                  </div>
                  <div className="rounded-xl border bg-background p-4" data-testid="tax-ogolne-card">
                    <div className="text-xs font-medium text-muted-foreground" data-testid="tax-ogolne-label">
                      Podatek do zapłaty (Zasady ogólne)
                    </div>
                    <div className="mt-2 text-xl font-semibold" data-testid="tax-ogolne-value">
                      {formatPLN(calc.nowyladzo)} zł
                    </div>
                  </div>
                  <div className="rounded-xl border bg-background p-4" data-testid="tax-liniowy-card">
                    <div className="text-xs font-medium text-muted-foreground" data-testid="tax-liniowy-label">
                      Podatek do zapłaty (Liniówka 19%)
                    </div>
                    <div className="mt-2 text-xl font-semibold" data-testid="tax-liniowy-value">
                      {formatPLN(calc.nowelintax)} zł
                    </div>
                  </div>
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-3" data-testid="health-grid">
                  <div className="rounded-xl border bg-background p-4" data-testid="health-ryczalt-card">
                    <div className="text-xs font-medium text-muted-foreground" data-testid="health-ryczalt-label">
                      Zdrowotne ryczałt (2026)
                    </div>
                    <div className="mt-2 text-xl font-semibold" data-testid="health-ryczalt-value">
                      {formatPLN(calc.nowezdrowotneryczalt)} zł
                    </div>
                  </div>
                  <div className="rounded-xl border bg-background p-4" data-testid="health-ogolne-card">
                    <div className="text-xs font-medium text-muted-foreground" data-testid="health-ogolne-label">
                      Zdrowotne ogólne (min. 432,54)
                    </div>
                    <div className="mt-2 text-xl font-semibold" data-testid="health-ogolne-value">
                      {formatPLN(calc.nowezdrowotneogolne)} zł
                    </div>
                  </div>
                  <div className="rounded-xl border bg-background p-4" data-testid="health-liniowy-card">
                    <div className="text-xs font-medium text-muted-foreground" data-testid="health-liniowy-label">
                      Zdrowotne liniowy (min. 432,54)
                    </div>
                    <div className="mt-2 text-xl font-semibold" data-testid="health-liniowy-value">
                      {formatPLN(calc.nowezdrowotneliniowy)} zł
                    </div>
                  </div>
                </div>

                <div className="mt-4 rounded-xl border bg-background p-4" data-testid="limit10200-card">
                  <div className="text-xs font-medium text-muted-foreground" data-testid="limit10200-label">
                    Limit 10 200 zł (liniówka) — ulga na podatku
                  </div>
                  <div className="mt-2 text-xl font-semibold" data-testid="limit10200-value">
                    {formatPLN(calc.limit10200)} zł
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground" data-testid="limit10200-hint">
                    Jeśli zdrowotne przekracza 10 200 zł/rok, wówczas należy odjąć tą kwotę od podatku w ciągu roku.
                  </div>
                </div>

                <div className="mt-4 grid gap-4 lg:grid-cols-3" data-testid="takehome-grid">
                  <div
                    className={`rounded-xl border bg-background p-4 ${
                      bestTakeHomeKey === "ryczalt" ? "border-emerald-400 bg-emerald-50" : ""
                    }`}
                    data-testid="takehome-ryczalt-card"
                  >
                    <div className="flex items-center justify-between gap-2" data-testid="takehome-ryczalt-header">
                      <div className="text-xs font-medium text-muted-foreground" data-testid="takehome-ryczalt-label">
                        Zostaje na rękę (ryczałt)
                      </div>
                      {bestTakeHomeKey === "ryczalt" ? (
                        <span
                          className="rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-medium text-white"
                          data-testid="takehome-ryczalt-best-badge"
                        >
                          Najlepsze
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-2 text-2xl font-semibold" data-testid="takehome-ryczalt-value">
                      {formatPLN(calc.zostajeRyczalt)} zł
                    </div>
                  </div>

                  <div
                    className={`rounded-xl border bg-background p-4 ${
                      bestTakeHomeKey === "ogolne" ? "border-emerald-400 bg-emerald-50" : ""
                    }`}
                    data-testid="takehome-ogolne-card"
                  >
                    <div className="flex items-center justify-between gap-2" data-testid="takehome-ogolne-header">
                      <div className="text-xs font-medium text-muted-foreground" data-testid="takehome-ogolne-label">
                        Zostaje na rękę (Zasady ogólne)
                      </div>
                      {bestTakeHomeKey === "ogolne" ? (
                        <span
                          className="rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-medium text-white"
                          data-testid="takehome-ogolne-best-badge"
                        >
                          Najlepsze
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-2 text-2xl font-semibold" data-testid="takehome-ogolne-value">
                      {formatPLN(calc.zostajeOgolne)} zł
                    </div>
                  </div>

                  <div
                    className={`rounded-xl border bg-background p-4 ${
                      bestTakeHomeKey === "liniowy" ? "border-emerald-400 bg-emerald-50" : ""
                    }`}
                    data-testid="takehome-liniowy-card"
                  >
                    <div className="flex items-center justify-between gap-2" data-testid="takehome-liniowy-header">
                      <div className="text-xs font-medium text-muted-foreground" data-testid="takehome-liniowy-label">
                        Zostaje na rękę (Liniówka)
                      </div>
                      {bestTakeHomeKey === "liniowy" ? (
                        <span
                          className="rounded-full bg-emerald-600 px-2 py-0.5 text-[11px] font-medium text-white"
                          data-testid="takehome-liniowy-best-badge"
                        >
                          Najlepsze
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-2 text-2xl font-semibold" data-testid="takehome-liniowy-value">
                      {formatPLN(calc.zostajeLiniowy)} zł
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 rounded-2xl border bg-background p-5" data-testid="feedback-section">
                <div className="flex items-start justify-between gap-4" data-testid="feedback-header">
                  <div>
                    <div className="text-sm font-semibold" data-testid="feedback-title">
                      Zostaw Feedback i kliknij zakończ
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground" data-testid="feedback-subtitle">
                      Skala 1–5 (5 — super)
                    </div>
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-5 gap-2" data-testid="feedback-grid">
                  {["1", "2", "3", "4", "5"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setFeedback(v)}
                      className={`rounded-lg border px-3 py-2 text-sm transition hover:bg-muted ${
                        feedback === v ? "border-primary bg-muted" : "border-border"
                      }`}
                      data-testid={`feedback-option-${v}-button`}
                    >
                      {v}
                    </button>
                  ))}
                </div>

                {submitError ? (
                  <div
                    className="mt-3 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm"
                    data-testid="submit-error"
                  >
                    {submitError}
                  </div>
                ) : null}

                <div className="mt-6 rounded-xl border bg-muted/20 p-4 text-sm" data-testid="deadline-info">
                  <div className="font-medium" data-testid="deadline-info-title">
                    Do kiedy można zmienić formę opodatkowania (JDG)?
                  </div>
                  <div className="mt-2 text-muted-foreground" data-testid="deadline-info-text">
                    Co do zasady, zmianę formy opodatkowania na dany rok wybiera się do <span className="font-medium">20. dnia miesiąca</span> następującego po miesiącu,
                    w którym uzyskano <span className="font-medium">pierwszy przychód</span> w danym roku (a jeśli pierwszy przychód był w grudniu — do końca roku).
                    Zawsze warto potwierdzić termin w aktualnych przepisach lub z księgowym.
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap items-center justify-between gap-3" data-testid="step2-actions">
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="rounded-lg border px-4 py-2 text-sm hover:bg-muted"
                    data-testid="step2-back-button"
                  >
                    Wstecz
                  </button>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleCopySummary}
                      className="rounded-lg border px-4 py-2 text-sm hover:bg-muted"
                      data-testid="copy-summary-button"
                    >
                      Kopiuj podsumowanie
                    </button>
                    <button
                      type="button"
                      onClick={handleFinish}
                      className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                      data-testid="finish-button"
                    >
                      Zakończ
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="space-y-6" data-testid="step-3">
            <Card
              title="Dziękujemy!"
              description="Kalkulacja została zakończona. (W tej wersji nie zapisujemy wyniku — to czysty kalkulator)."
              testId="done-card"
            >
              <div className="grid gap-4" data-testid="done-content">
                <div className="rounded-xl border bg-muted/20 p-4" data-testid="done-summary">
                  <div className="text-sm font-semibold" data-testid="done-best-title">
                    Najlepsza opcja wg „zostaje na rękę”:
                  </div>
                  <div className="mt-2 text-2xl font-semibold" data-testid="done-best-value">
                    {bestOption.label} — {formatPLN(bestOption.value)} zł
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground" data-testid="done-ryczalt-rate">
                    Użyta stawka ryczałtu: <span className="font-medium">{formatPercent(effectiveRyczaltRate * 100)}</span>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground" data-testid="done-feedback-value">
                    Twój feedback: <span className="font-medium">{feedback}/5</span>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-2" data-testid="done-actions">
                  <button
                    type="button"
                    onClick={handleCopySummary}
                    className="rounded-lg border px-4 py-2 text-sm hover:bg-muted"
                    data-testid="done-copy-summary-button"
                  >
                    Kopiuj podsumowanie
                  </button>
                  <button
                    type="button"
                    onClick={handleStartOver}
                    className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                    data-testid="done-start-over-button"
                  >
                    Zacznij od nowa
                  </button>
                </div>
              </div>
            </Card>
          </div>
        ) : null}

        <footer className="mt-10 text-xs text-muted-foreground" data-testid="page-footer">
          Podpowiedź: możesz wkleić wartości z przecinkiem (np. 12,5). Nie zapisujemy danych.
        </footer>
      </div>
    </div>
  );
}
